/**
 * Fechas de calendario del tarifario.
 *
 * Una vigencia, una fecha estimada de embarque o el fin de un período son
 * **días del calendario**, no instantes: "vence el 31 de diciembre" nombra un
 * día entero, sin hora ni huso. La base las guarda a las 00:00 y la API las
 * manda como `2026-12-31T00:00:00.000Z`.
 *
 * Formatearlas con `new Date(iso).toLocaleDateString(...)` las lee como un
 * instante en UTC y las dibuja en la hora del navegador. En Bolivia, cuatro
 * horas antes: **cada fecha aparecía un día antes del que dice el Excel**. Una
 * tarifa "hasta el 21-jul" se mostraba como "venció 20-jul", y quien la
 * comparaba contra su tarifario veía un número que no coincidía.
 *
 * Acá se leen en UTC, que es como fueron escritas, así el día que se muestra es
 * el día que se cargó — en cualquier país y sin importar el huso del navegador.
 */

const LOCALE = 'es-BO';

/** Siempre UTC: la fecha guardada ES el día, no un momento que haya que convertir. */
const CALENDAR: Intl.DateTimeFormatOptions = { timeZone: 'UTC' };

/** `09-ago` — para tablas, donde el año se sobreentiende. */
export function formatShortDate(iso: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(iso);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, { ...CALENDAR, day: '2-digit', month: 'short' });
}

/** `09-08` — para cuando el espacio es mínimo. */
export function formatNumericDate(iso: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(iso);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, { ...CALENDAR, day: '2-digit', month: '2-digit' });
}

/** `9/8/2026` — cuando el año importa, como en un aviso de vencimiento. */
export function formatFullDate(iso: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(iso);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, CALENDAR);
}

/**
 * `YYYY-MM-DD` para un `<input type="date">`.
 *
 * Se corta la cadena en vez de pasar por `Date` justamente para no volver a
 * meter el huso en el medio.
 */
export function toDateInputValue(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  if (iso instanceof Date) return iso.toISOString().slice(0, 10);
  return iso.slice(0, 10);
}

/** Una fecha ilegible se trata como ausente: mejor un guion que "Invalid Date". */
function toDate(iso: string | Date | null | undefined): Date | null {
  if (!iso) return null;
  const d = iso instanceof Date ? iso : new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}
